import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"
import mermaid from "mermaid"

export default class extends Controller {
  static targets = ["input", "preview", "previewButton"]

  connect() {
    this.previewButtonTarget.addEventListener("click", () => this.togglePreview())
    
    // Initialize Mermaid with configuration
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'loose',
      fontFamily: 'inherit'
    })

    // Configure marked options
    marked.setOptions({
      breaks: true,
      gfm: true
    })
  }

  togglePreview() {
    const isHidden = this.previewTarget.classList.contains("hidden")
    
    if (isHidden) {
      this.previewTarget.classList.remove("hidden")
      this.previewButtonTarget.textContent = "Hide Preview"
      this.updatePreview()
    } else {
      this.previewTarget.classList.add("hidden")
      this.previewButtonTarget.textContent = "Preview"
    }
  }

  async updatePreview() {
    const markdown = this.inputTarget.value
    
    // Parse markdown with mermaid code blocks handling
    const html = this.parseMarkdownWithMermaid(markdown)
    this.previewTarget.innerHTML = html
    
    // Render all mermaid diagrams
    await this.renderMermaidDiagrams()
  }

  parseMarkdownWithMermaid(markdown) {
    // Replace mermaid code blocks with placeholder divs
    let mermaidIndex = 0
    const processedMarkdown = markdown.replace(/```mermaid\n([\s\S]*?)\n```/g, (match, code) => {
      const id = `mermaid-${mermaidIndex++}`
      // Store the mermaid code in a data attribute for later rendering
      return `<div class="mermaid-diagram" id="${id}" data-mermaid-code="${this.escapeHtml(code.trim())}"></div>`
    })
    
    return marked.parse(processedMarkdown)
  }

  async renderMermaidDiagrams() {
    const mermaidElements = this.previewTarget.querySelectorAll('.mermaid-diagram')
    
    for (const element of mermaidElements) {
      const code = element.getAttribute('data-mermaid-code')
      const id = element.id
      
      try {
        // Render the mermaid diagram
        const { svg } = await mermaid.render(`${id}-svg`, code)
        element.innerHTML = svg
        element.removeAttribute('data-mermaid-code')
      } catch (error) {
        console.error('Mermaid rendering error:', error)
        element.innerHTML = `<div class="text-red-500 p-4 border border-red-300 rounded">
          <strong>Mermaid Error:</strong> ${error.message}
          <pre class="mt-2 text-sm bg-red-50 p-2 rounded">${this.escapeHtml(code)}</pre>
        </div>`
      }
    }
  }

  applySyntaxHighlighting() {
    // Apply highlighting to any code blocks that weren't already processed
    const codeBlocks = this.previewTarget.querySelectorAll('pre code:not(.hljs)')
    codeBlocks.forEach(block => {
      hljs.highlightElement(block)
    })
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Update preview when content changes
  async input() {
    if (!this.previewTarget.classList.contains("hidden")) {
      await this.updatePreview()
    }
  }
} 